/**
 * The drill-in surface for every screen.
 *
 * Drill-ins used to render as an extra card appended to the page. That card
 * appeared below the fold on a long account page, so clicking a month looked
 * like nothing had happened, and the page then had two competing "current"
 * contexts -- the range at the top and the focused month somewhere further
 * down. A modal makes the drill-in a state you are in and then leave.
 *
 * Wide by design: these hold financial tables, and a default dialog width would
 * force the columns into a horizontal scroll.
 */
import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function DrillInDialog({
  open,
  onOpenChange,
  title,
  description,
  action,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /**
   * Where this drill-in leads, rendered beside the title.
   *
   * A drill-in whose subject has a page of its own has to offer it. The month
   * drill-in got this for free -- its rows are projects, and each name is a
   * link -- but a drill-in ABOUT one project has no such row, so the reader
   * had to close the modal and go hunting for the project they were already
   * looking at.
   *
   * In the header, not a footer: the content area scrolls, so a footer action
   * sits below however many months the table holds. The header does not move.
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] w-[900px] max-w-[92vw] overflow-y-auto">
        <DialogHeader>
          {/* pr-8 keeps the action clear of Radix's own close button, which is
              absolutely positioned in the same top-right corner. */}
          <div className="flex items-start justify-between gap-4 pr-8">
            <DialogTitle>{title}</DialogTitle>
            {action ? <div className="shrink-0">{action}</div> : null}
          </div>
          {/* Always rendered, even when empty: Radix warns when a dialog has no
              description, and the warning is noise that hides real ones. */}
          <DialogDescription>{description ?? ""}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}
